import { useState, useCallback, useMemo } from 'react';
import { getRace, getClass, getBackground, getSpell } from '@character-creator/data';
import { getAllSkillModifiers, getAllSavingThrowModifiers, getPassiveScore, getArmorClass } from '@character-creator/core';
import {
  getGenerator,
  getTemplatesForRuleset,
  getDefaultTemplateForRuleset,
  type TemplateConfig,
} from '../services/pdf';
import type { CharacterSheetData, PdfExportOptions } from '../services/pdf';
import { useCharacterBenefits, type CharacterBenefits } from './useCharacterBenefits';
import { useCharacterCreationStore, type CharacterDraft } from '../stores/characterCreationStore';
import { formatAbility } from '../utils/formatting';

// Format equipment item ID to display name
function formatEquipmentName(id: string): string {
  return id
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Consolidate duplicate equipment items (e.g., 4x javelin -> "Javelin (4)")
function consolidateEquipment(items: string[]): string[] {
  const counts: Record<string, number> = {};
  for (const item of items) {
    counts[item] = (counts[item] || 0) + 1;
  }

  return Object.entries(counts).map(([item, count]) => {
    return count > 1 ? `${item} (${count})` : item;
  });
}

// Format familiar speed for PDF export
function formatFamiliarSpeed(speed: { walk?: number; fly?: number; swim?: number; climb?: number; burrow?: number }): string {
  const parts: string[] = [];
  if (speed.walk) parts.push(`${speed.walk} ft`);
  if (speed.fly) parts.push(`fly ${speed.fly} ft`);
  if (speed.swim) parts.push(`swim ${speed.swim} ft`);
  if (speed.climb) parts.push(`climb ${speed.climb} ft`);
  if (speed.burrow) parts.push(`burrow ${speed.burrow} ft`);
  return parts.join(', ') || '0 ft';
}

/**
 * Transform CharacterDraft and CharacterBenefits into CharacterSheetData for PDF
 */
function transformToSheetData(
  draft: CharacterDraft,
  benefits: CharacterBenefits
): CharacterSheetData {
  const race = draft.raceId ? getRace(draft.raceId) : null;
  const subrace = race?.subraces?.find((s) => s.id === draft.subraceId);
  const primaryClass = draft.classId ? getClass(draft.classId) : null;
  const background = draft.backgroundId ? getBackground(draft.backgroundId) : null;

  // Format class/level string (handles multiclass)
  const classLevelStr =
    benefits.classLevels.length > 0
      ? benefits.classLevels.map((cl) => `${cl.className} ${cl.level}`).join(' / ')
      : 'Commoner 1';

  // Format race string
  const raceStr = race ? (subrace ? `${subrace.name} ${race.name}` : race.name) : '';

  // Format alignment
  const alignmentStr = draft.alignment
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  // Build abilities object
  const abilities: CharacterSheetData['abilities'] = {
    strength: { score: benefits.finalScores.strength, modifier: benefits.modifiers.strength },
    dexterity: { score: benefits.finalScores.dexterity, modifier: benefits.modifiers.dexterity },
    constitution: {
      score: benefits.finalScores.constitution,
      modifier: benefits.modifiers.constitution,
    },
    intelligence: {
      score: benefits.finalScores.intelligence,
      modifier: benefits.modifiers.intelligence,
    },
    wisdom: { score: benefits.finalScores.wisdom, modifier: benefits.modifiers.wisdom },
    charisma: { score: benefits.finalScores.charisma, modifier: benefits.modifiers.charisma },
  };

  // Compute derived stats via core helpers
  const skillModifiers = getAllSkillModifiers(benefits);
  const savingThrowModifiers = getAllSavingThrowModifiers(benefits);
  const passivePerception = getPassiveScore(benefits, 'perception');

  // Format proficiencies and languages
  const proficienciesAndLanguages = [
    `Languages: ${benefits.languages.join(', ') || 'Common'}`,
    benefits.armor.length > 0 ? `Armor: ${benefits.armor.join(', ')}` : '',
    benefits.weapons.length > 0 ? `Weapons: ${benefits.weapons.join(', ')}` : '',
    benefits.tools.length > 0 ? `Tools: ${benefits.tools.join(', ')}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  // Format features and traits (includes racial traits, class features, feats, and class-specific choices)
  const featuresAndTraitsList: string[] = [
    ...benefits.racialTraits.map((t) => `${t.name}\n${t.description}`),
    ...benefits.classFeatures.map((f) => `${f.name} (Level ${f.level})\n${f.description}`),
    ...benefits.feats.map((f) => `FEAT: ${f.name}\n${f.description}\n• ${f.benefits.join('\n• ')}`),
  ];

  // Add Pact Boon (Warlock)
  if (benefits.pactBoon) {
    featuresAndTraitsList.push(`Pact Boon: ${benefits.pactBoon.name}\n${benefits.pactBoon.description}`);
  }

  // Add Eldritch Invocations (Warlock)
  if (benefits.eldritchInvocations.length > 0) {
    featuresAndTraitsList.push(`Eldritch Invocations: ${benefits.eldritchInvocations.map((i) => i.name).join(', ')}`);
  }

  // Add Metamagic (Sorcerer)
  if (benefits.metamagicChoices.length > 0) {
    featuresAndTraitsList.push(`Metamagic: ${benefits.metamagicChoices.map((m) => m.name).join(', ')}`);
  }

  // Add Favored Enemies (Ranger)
  if (benefits.favoredEnemies.length > 0) {
    featuresAndTraitsList.push(`Favored Enemies: ${benefits.favoredEnemies.map((e) => e.name).join(', ')}`);
  }

  // Add Natural Explorer Terrains (Ranger)
  if (benefits.naturalExplorerTerrains.length > 0) {
    featuresAndTraitsList.push(`Natural Explorer: ${benefits.naturalExplorerTerrains.map((t) => t.name).join(', ')}`);
  }

  // Add Land Circle Terrain (Druid)
  if (benefits.landCircleTerrain) {
    featuresAndTraitsList.push(`Circle of the Land: ${benefits.landCircleTerrain.name}`);
  }

  const featuresAndTraits = featuresAndTraitsList.join('\n\n');

  // Format familiar info if selected
  const familiarInfo = benefits.familiar
    ? `FAMILIAR: ${benefits.familiar.name}\nAC ${benefits.familiar.armorClass}, HP ${benefits.familiar.hitPoints}, ${formatFamiliarSpeed(benefits.familiar.speed)}`
    : '';

  // Hit dice string
  const hitDiceStr =
    benefits.hitDice.map((hd) => `${hd.count}${hd.die}`).join(' + ') || '1d4';

  // Build equipment list
  const equipmentList: string[] = [];

  // Add class equipment (selected options for choices, plus fixed items)
  if (primaryClass) {
    primaryClass.startingEquipment.forEach((item, index) => {
      if (typeof item === 'string') {
        // Fixed item
        equipmentList.push(formatEquipmentName(item));
      } else if ('choose' in item && 'from' in item) {
        // Choice - use the selected option
        const selectedOptionIndex = draft.equipmentSelections[index] ?? 0;
        const selectedOption = item.from[selectedOptionIndex];
        if (selectedOption) {
          equipmentList.push(selectedOption.map(formatEquipmentName).join(' and '));
        }
      }
    });
  }

  // Add background equipment
  if (background) {
    equipmentList.push(...background.equipment);
  }

  // Add custom equipment
  if (draft.customEquipment && draft.customEquipment.length > 0) {
    equipmentList.push(...draft.customEquipment);
  }

  // Consolidate and format equipment string
  const equipmentStr = consolidateEquipment(equipmentList).join('\n');

  return {
    characterName: draft.name,
    classLevel: classLevelStr,
    background: background?.name || '',
    playerName: '', // User can fill in PDF
    race: raceStr,
    alignment: alignmentStr,
    experiencePoints: '', // User can fill in PDF

    abilities,

    proficiencyBonus: benefits.proficiencyBonus,
    armorClass: getArmorClass(benefits),
    initiative: benefits.modifiers.dexterity,
    speed: benefits.speed,
    hitPointMaximum: benefits.hitPoints,
    hitPointCurrent: String(benefits.hitPoints), // Start at max
    hitPointTemp: '',
    hitDiceTotal: String(benefits.characterLevel),
    hitDice: hitDiceStr,

    savingThrowProficiencies: benefits.savingThrows,
    skillProficiencies: benefits.skills,
    skillModifiers,
    savingThrowModifiers,
    passivePerception,

    proficienciesAndLanguages,
    featuresAndTraits,

    equipment: equipmentStr,
    currency: draft.currency ?? { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },

    attacks: draft.attacks ?? [],

    personalityTraits: draft.personalityTraits.join('\n'),
    ideals: draft.ideals.join('\n'),
    bonds: draft.bonds.join('\n'),
    flaws: draft.flaws.join('\n'),

    backstory: draft.backstory,
    appearance: {
      age: draft.appearance.age || '',
      height: draft.appearance.height || '',
      weight: draft.appearance.weight || '',
      eyes: draft.appearance.eyes || '',
      skin: draft.appearance.skin || '',
      hair: draft.appearance.hair || '',
    },
    allies: '', // User can fill in PDF
    additionalFeaturesTraits: familiarInfo, // Include familiar info if selected
    treasure: '', // User can fill in PDF

    spellcasting: benefits.spellcasting
      ? (() => {
          // Organize spells by level
          const spellsByLevel: string[][] = Array.from({ length: 10 }, () => []);

          // Add cantrips (level 0)
          for (const spellId of draft.selectedCantrips) {
            const spell = getSpell(spellId);
            if (spell) {
              spellsByLevel[0].push(spell.name);
            }
          }

          // Add known spells (levels 1-9)
          for (const spellId of draft.knownSpells) {
            const spell = getSpell(spellId);
            if (spell && spell.level >= 1 && spell.level <= 9) {
              spellsByLevel[spell.level].push(spell.name);
            }
          }

          // Add Circle Spells from Circle of the Land (always prepared)
          if (benefits.landCircleTerrain?.circleSpells) {
            for (const spellId of benefits.landCircleTerrain.circleSpells) {
              const spell = getSpell(spellId);
              if (spell && spell.level >= 1 && spell.level <= 9) {
                // Mark as circle spell with asterisk
                const spellName = `${spell.name}*`;
                if (!spellsByLevel[spell.level].includes(spellName)) {
                  spellsByLevel[spell.level].push(spellName);
                }
              }
            }
          }

          // Build spell slots array from class data
          // Compute characterLevel from classLevels (source of truth) to avoid persist desync bug
          const characterLevel = draft.classLevels.length > 0
            ? draft.classLevels.reduce((sum, cl) => sum + cl.level, 0)
            : draft.characterLevel;
          const spellSlots: { total: number; used: number }[] = [];
          if (primaryClass?.spellcasting?.spellSlots) {
            const levelIndex = Math.min(characterLevel, 20) - 1;
            const slotsAtLevel = primaryClass.spellcasting.spellSlots[levelIndex];
            if (slotsAtLevel) {
              for (let i = 0; i < 9; i++) {
                spellSlots.push({ total: slotsAtLevel[i] || 0, used: 0 });
              }
            }
          }

          return {
            spellcastingClass: primaryClass?.name || '',
            spellcastingAbility: formatAbility(benefits.spellcasting.ability).slice(0, 3).toUpperCase(),
            spellSaveDC: benefits.spellcasting.saveDC,
            spellAttackBonus: benefits.spellcasting.attackBonus,
            spellSlots,
            spellsByLevel,
          };
        })()
      : undefined,
  };
}

export interface UsePdfExportResult {
  /** Export PDF with optional template selection */
  exportPdf: (options?: PdfExportOptions) => Promise<void>;
  /** Whether PDF is currently being generated */
  isGenerating: boolean;
  /** Error message if generation failed */
  error: string | null;
  /** Available templates for the current ruleset */
  availableTemplates: TemplateConfig[];
  /** Default template for the current ruleset */
  defaultTemplate: TemplateConfig | undefined;
  /** Current ruleset ID */
  rulesetId: string;
}

/**
 * Hook for exporting character data to PDF
 *
 * Provides template selection based on the character's ruleset:
 * - Ignus: Arcana, Pragmata
 * - SRD 5.1: Arcana, Pragmata, D&D Official
 */
export function usePdfExport(): UsePdfExportResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { draft } = useCharacterCreationStore();
  const benefits = useCharacterBenefits();

  // Get available templates for the current ruleset
  const rulesetId = draft.rulesetId || 'srd-5.1';

  const availableTemplates = useMemo(
    () => getTemplatesForRuleset(rulesetId),
    [rulesetId]
  );

  const defaultTemplate = useMemo(
    () => getDefaultTemplateForRuleset(rulesetId),
    [rulesetId]
  );

  const exportPdf = useCallback(
    async (options: PdfExportOptions = {}) => {
      setIsGenerating(true);
      setError(null);

      try {
        const generator = getGenerator();
        const sheetData = transformToSheetData(draft, benefits);

        // Use provided templateId or fall back to default
        const templateId = options.templateId || defaultTemplate?.id;

        await generator.downloadPdf(
          sheetData,
          {
            filename: `${draft.name || 'character'}-character-sheet.pdf`,
            ...options,
            templateId,
          },
          rulesetId
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to generate PDF';
        setError(message);
        console.error('PDF export error:', err);
      } finally {
        setIsGenerating(false);
      }
    },
    [draft, benefits, defaultTemplate, rulesetId]
  );

  return {
    exportPdf,
    isGenerating,
    error,
    availableTemplates,
    defaultTemplate,
    rulesetId,
  };
}
